import { logError } from '../lib/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Bell, Settings, LogOut, User, Home as HomeIcon, HelpCircle, Phone, Key, Briefcase, CalendarDays, BookOpenCheck, Award, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import PushToggle from './header/PushToggle';
import BrandLogo from './BrandLogo';
import { APP_NAME, APP_TITLE_PREFIX, APP_TITLE_SUFFIX, IS_MAGIC_TEAM } from '../lib/brand';
import NotificationItem from './header/NotificationItem';
import NotificationPopup from './NotificationPopup';
import { isMediaModalOpen } from '../lib/mediaModal';
import { pollIntervalMs } from '../lib/pollJitter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLD_START_POPUP_DELAY_MS = 1500;
const NOTIF_POLL_VISIBLE_MS = 90000;
const NOTIF_POLL_HIDDEN_MS = 300000;
const NOTIF_POLL_JITTER_MS = 30000;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, canAccessBusiness } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [popupNotification, setPopupNotification] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAdminPwdModal, setShowAdminPwdModal] = useState(false);
  const [adminCurrentPassword, setAdminCurrentPassword] = useState('');
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [adminPwdError, setAdminPwdError] = useState('');
  const [adminPwdSuccess, setAdminPwdSuccess] = useState('');
  const [progressSnapshot, setProgressSnapshot] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const notifRef = useRef(null);
  /** Ids already queued for in-session popups (don't enqueue duplicates on poll). */
  const sessionPopupEnqueuedRef = useRef(new Set());
  const popupQueueRef = useRef([]);
  const notificationsRef = useRef([]);
  const popupNotificationRef = useRef(null);
  const coldStartTimerRef = useRef(null);
  const feedEtagRef = useRef(null);
  const appForegroundSinceRef = useRef(Date.now());
  const lastForegroundNotifFetchRef = useRef(0);
  const pollTimerRef = useRef(null);
  const [mediaModalOpen, setMediaModalOpenState] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const sync = () => setMediaModalOpenState(isMediaModalOpen());
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-mtc-media-open'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);
  useEffect(() => {
    popupNotificationRef.current = popupNotification;
  }, [popupNotification]);

  const syncAppBadge = async (count) => {
    if (!('setAppBadge' in navigator)) return;
    try {
      if (count > 0) await navigator.setAppBadge(count);
      else await navigator.clearAppBadge?.();
    } catch (e) {
      logError('Header:badge', e);
    }
  };

  const takeNextPopupFromQueue = (list) => {
    while (popupQueueRef.current.length) {
      const id = popupQueueRef.current.shift();
      const n = list.find((x) => x.id === id);
      if (n && !n.read) return n;
    }
    return null;
  };

  const applyReadState = (notifId) => {
    const prevList = notificationsRef.current;
    const wasUnread = prevList.some((n) => n.id === notifId && !n.read);
    const nextList = prevList.map((n) => (n.id === notifId ? { ...n, read: true } : n));
    notificationsRef.current = nextList;
    setNotifications(nextList);
    popupQueueRef.current = popupQueueRef.current.filter((id) => id !== notifId);
    if (wasUnread) {
      setUnreadCount((c) => {
        const next = Math.max(0, c - 1);
        void syncAppBadge(next);
        return next;
      });
    }
    return nextList;
  };

  const fetchUserProgress = useCallback(async () => {
    if (!user) return;
    setProgressLoading(true);
    try {
      const r = await axios.get(`${API}/progress/me`);
      setProgressSnapshot(r.data);
    } catch (err) {
      logError('Header', err);
      setProgressSnapshot(null);
    } finally {
      setProgressLoading(false);
    }
  }, [user]);

  const tryShowPopupFromQueue = useCallback((list, { immediate = false } = {}) => {
    if (isMediaModalOpen()) {
      setPopupNotification(null);
      return;
    }
    if (!immediate) {
      const elapsed = Date.now() - appForegroundSinceRef.current;
      if (elapsed < COLD_START_POPUP_DELAY_MS) {
        const delay = COLD_START_POPUP_DELAY_MS - elapsed;
        if (coldStartTimerRef.current) clearTimeout(coldStartTimerRef.current);
        coldStartTimerRef.current = window.setTimeout(() => {
          coldStartTimerRef.current = null;
          if (isMediaModalOpen()) return;
          if (popupNotificationRef.current) return;
          const next = takeNextPopupFromQueue(notificationsRef.current);
          if (next) setPopupNotification(next);
        }, delay + 50);
        return;
      }
    }
    setPopupNotification((prev) => {
      if (prev) return prev;
      return takeNextPopupFromQueue(list ?? notificationsRef.current);
    });
  }, []);

  const fetchNotifications = useCallback(async ({ force = false } = {}) => {
    try {
      const headers = {};
      if (!force && feedEtagRef.current) headers['If-None-Match'] = feedEtagRef.current;
      const res = await axios.get(`${API}/notifications/feed`, {
        headers,
        validateStatus: (s) => s === 200 || s === 304,
      });
      if (res.status === 304) return;

      const etag = res.headers.etag || res.headers.ETag;
      if (etag) feedEtagRef.current = etag;

      const list = res.data?.notifications ?? [];
      const count = res.data?.unread_count ?? 0;
      notificationsRef.current = list;
      setNotifications(list);
      setUnreadCount(count);
      await syncAppBadge(count);

      const newUnread = list.filter((n) => !n.read && !sessionPopupEnqueuedRef.current.has(n.id));
      newUnread.forEach((n) => {
        sessionPopupEnqueuedRef.current.add(n.id);
        popupQueueRef.current.push(n.id);
      });

      tryShowPopupFromQueue(list);
    } catch (err) {
      logError('Header', err);
    }
  }, [tryShowPopupFromQueue]);

  /** Push SW → popup immediato (non aspettare il poll da 45s). */
  const handlePushReceived = useCallback((event) => {
    const pushData = event.data?.data;
    if (!pushData) return;

    feedEtagRef.current = null;
    const notifId = pushData.notif_id;
    if (notifId) {
      const instantNotif = {
        id: notifId,
        title: pushData.title || APP_NAME,
        body: pushData.body || '',
        url: pushData.url || '/',
        is_banner: !!pushData.is_banner,
        image_path: pushData.image_path || null,
        banner_expires_at: pushData.banner_expires_at || null,
        read: false,
      };
      const prevList = notificationsRef.current;
      const hasNotif = prevList.some((n) => n.id === notifId);

      if (!hasNotif) {
        const nextList = [instantNotif, ...prevList];
        notificationsRef.current = nextList;
        setNotifications(nextList);
      }

      if (typeof pushData.unread_count === 'number') {
        setUnreadCount(pushData.unread_count);
        void syncAppBadge(pushData.unread_count);
      } else if (!hasNotif) {
        setUnreadCount((c) => {
          const next = c + 1;
          void syncAppBadge(next);
          return next;
        });
      }
      if (!sessionPopupEnqueuedRef.current.has(notifId)) {
        sessionPopupEnqueuedRef.current.add(notifId);
        popupQueueRef.current.push(notifId);
      }
      tryShowPopupFromQueue(notificationsRef.current, { immediate: true });
    }
  }, [tryShowPopupFromQueue]);

  const scheduleForegroundNotificationFetch = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastForegroundNotifFetchRef.current < 700) return;
    lastForegroundNotifFetchRef.current = now;
    feedEtagRef.current = null;
    void fetchNotifications({ force: true });
  }, [fetchNotifications]);

  useEffect(() => {
    const scheduleNextPoll = () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      const base = document.visibilityState === 'visible' ? NOTIF_POLL_VISIBLE_MS : NOTIF_POLL_HIDDEN_MS;
      pollTimerRef.current = window.setTimeout(() => {
        void fetchNotifications().finally(scheduleNextPoll);
      }, pollIntervalMs(base, NOTIF_POLL_JITTER_MS));
    };

    void fetchNotifications().finally(scheduleNextPoll);

    const handleVisibility = () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      scheduleNextPoll();
      if (document.visibilityState === 'visible') {
        appForegroundSinceRef.current = Date.now();
        scheduleForegroundNotificationFetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', scheduleForegroundNotificationFetch);

    const handleSwMessage = (event) => {
      if (event.data?.type === 'push-received') handlePushReceived(event);
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
    }

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (coldStartTimerRef.current) clearTimeout(coldStartTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', scheduleForegroundNotificationFetch);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      }
    };
  }, [fetchNotifications, scheduleForegroundNotificationFetch, handlePushReceived]);

  // Quando si chiude un modale video/documento, mostra la prossima notifica in coda.
  useEffect(() => {
    if (mediaModalOpen) {
      setPopupNotification(null);
      return;
    }
    if (popupNotificationRef.current) return;
    const next = takeNextPopupFromQueue(notificationsRef.current);
    if (next) setPopupNotification(next);
  }, [mediaModalOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /** Optimistic: aggiorna subito UI, poi POST; rollback se fallisce. */
  const markOneRead = (notifId) => {
    const prevList = notificationsRef.current;
    const prevUnread = prevList.filter((n) => !n.read).length;
    const nextList = applyReadState(notifId);
    if (popupNotificationRef.current?.id === notifId) {
      setPopupNotification(takeNextPopupFromQueue(nextList));
    }
    feedEtagRef.current = null;
    axios.post(`${API}/notifications/${notifId}/read`).catch((err) => {
      logError('Header', err);
      notificationsRef.current = prevList;
      setNotifications(prevList);
      sessionPopupEnqueuedRef.current.delete(notifId);
      if (!popupQueueRef.current.includes(notifId)) {
        popupQueueRef.current.unshift(notifId);
      }
      setUnreadCount(prevUnread);
      void syncAppBadge(prevUnread);
    });
  };

  /** Tap / close on the slide-down notification marks it read (same as opening the bell). */
  const dismissNotificationPopup = () => {
    const cur = popupNotificationRef.current;
    if (!cur?.id) {
      setPopupNotification(takeNextPopupFromQueue(notificationsRef.current));
      return;
    }
    if (!IS_MAGIC_TEAM && cur.is_banner && cur.image_path && cur.banner_expires_at) {
      // Notifica alla Home (già montata, non solo al prossimo mount) che questo
      // banner può comparire subito nella fascia trasversale.
      window.dispatchEvent(new CustomEvent('mtc-banner-ready', { detail: { ...cur, read: true } }));
    }
    markOneRead(cur.id);
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      const next = notificationsRef.current.map((n) => ({ ...n, read: true }));
      notificationsRef.current = next;
      setNotifications(next);
      setUnreadCount(0);
      popupQueueRef.current = [];
      sessionPopupEnqueuedRef.current.clear();
      setPopupNotification(null);
      feedEtagRef.current = null;
      await syncAppBadge(0);
    } catch (err) {
      logError('Header', err);
    }
  };

  const deleteNotification = (notifId) => {
    const prevList = notificationsRef.current;
    const removed = prevList.find((n) => n.id === notifId);
    const nextList = prevList.filter((n) => n.id !== notifId);
    const nextUnread = removed && !removed.read ? Math.max(0, unreadCount - 1) : unreadCount;
    notificationsRef.current = nextList;
    setNotifications(nextList);
    setUnreadCount(nextUnread);
    popupQueueRef.current = popupQueueRef.current.filter((id) => id !== notifId);
    sessionPopupEnqueuedRef.current.delete(notifId);
    if (popupNotificationRef.current?.id === notifId) {
      setPopupNotification(takeNextPopupFromQueue(nextList));
    }
    feedEtagRef.current = null;
    void syncAppBadge(nextUnread);
    axios.delete(`${API}/notifications/${notifId}`).catch((err) => {
      logError('Header:deleteNotification', err);
      notificationsRef.current = prevList;
      setNotifications(prevList);
      const restoredUnread = prevList.filter((n) => !n.read).length;
      setUnreadCount(restoredUnread);
      void syncAppBadge(restoredUnread);
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const mainProg = progressSnapshot?.main;
  const spilleProg = progressSnapshot?.spille;
  const showUserProgress =
    !!user &&
    (progressLoading || (mainProg?.catalog_total ?? 0) > 0 || (spilleProg?.catalog_total ?? 0) > 0);

  const handleChangeDefaultAdminPassword = async (e) => {
    e.preventDefault();
    setAdminPwdError('');
    setAdminPwdSuccess('');

    if (!adminCurrentPassword || !adminNewPassword) {
      setAdminPwdError('Compila tutti i campi');
      return;
    }

    if (adminNewPassword.length < 6) {
      setAdminPwdError('La nuova password deve avere almeno 6 caratteri');
      return;
    }

    try {
      await axios.post(`${API}/admin/change-default-password`, {
        current_password: adminCurrentPassword,
        new_password: adminNewPassword,
      });
      setAdminPwdSuccess('Password aggiornata');
      setAdminCurrentPassword('');
      setAdminNewPassword('');
      setTimeout(() => {
        setShowAdminPwdModal(false);
        setAdminPwdSuccess('');
      }, 1200);
    } catch (error) {
      setAdminPwdError(error.response?.data?.detail || 'Errore nel cambio password');
    }
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-zinc-200 safe-top">
      <div className="max-w-7xl mx-auto py-3 flex items-center justify-between gap-3 sm:gap-4 pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))]">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          data-testid="header-logo-btn"
        >
          <BrandLogo size={48} radius={12} />
          <div className="hidden sm:block text-left">
            <h1 className="text-xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#D4AF37]">{APP_TITLE_PREFIX}</span> {APP_TITLE_SUFFIX}
            </h1>
          </div>
        </button>

        {/* Desktop primary nav (hidden on mobile — BottomNav handles that) */}
        <nav className="hidden md:flex items-center gap-1 ml-auto mr-2">
          {[
            { to: '/', Icon: HomeIcon, label: 'Home', testid: 'desktop-nav-home' },
            { to: '/spille', Icon: Award, label: 'Spille', testid: 'desktop-nav-spille' },
            { to: '/carriera', Icon: Briefcase, label: 'Carriera', testid: 'desktop-nav-carriera' },
            { to: '/calendario', Icon: CalendarDays, label: 'Calendario', testid: 'desktop-nav-calendario' },
            { to: '/contatti', Icon: Phone, label: 'Contatti', testid: 'desktop-nav-contatti' },
            { to: '/faq', Icon: HelpCircle, label: 'FAQ', testid: 'desktop-nav-faq' },
            ...(canAccessBusiness ? [{ to: '/business', Icon: Building2, label: 'Business', testid: 'desktop-nav-business' }] : []),
          ].map(({ to, Icon, label, testid }) => {
            const active = location.pathname === to || (to === '/business' && location.pathname.startsWith('/business'));
            return (
              <button
                key={to}
                onClick={() => navigate(to)}
                data-testid={testid}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative" ref={notifRef}>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 relative"
              onClick={() => setShowNotifications(!showNotifications)}
              data-testid="notifications-btn"
            >
              <Bell className="!w-6 !h-6" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[20px] h-[20px] px-1 bg-[#D4AF37] text-white text-[11px] font-semibold rounded-full flex items-center justify-center" data-testid="notif-badge">
                  {unreadCount}
                </span>
              )}
            </Button>

            {showNotifications && (
              <div className="fixed left-2 right-2 top-[max(4.75rem,calc(env(safe-area-inset-top,0px)+3.75rem))] md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-80 bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden animate-fade-in">
                <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900">Notifiche</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-[#D4AF37] hover:text-[#B8941F] font-medium"
                      data-testid="mark-all-read-btn"
                    >
                      Segna tutte come lette
                    </button>
                  )}
                </div>

                {<PushToggle />}
                <div className="max-h-[32rem] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="p-6 text-center text-zinc-500 text-sm">Nessuna notifica</p>
                  ) : (
                    notifications.map((notif) => (
                      <NotificationItem
                        key={notif.id}
                        notif={notif}
                        onMarkRead={markOneRead}
                        onDelete={isAdmin ? deleteNotification : undefined}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <DropdownMenu onOpenChange={(open) => { if (open) fetchUserProgress(); }}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 h-11 px-2 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100" data-testid="user-menu-btn">
                <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <span className="hidden sm:inline text-sm">{user?.name || 'Utente'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 bg-white border-zinc-200">
              <div className="px-3 py-3 border-b border-zinc-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-zinc-900 text-sm truncate" data-testid="menu-user-name">{user?.name || 'Utente'}</p>
                    <p className="text-zinc-500 text-xs truncate" data-testid="menu-user-email">{user?.email || ''}</p>
                    {user?.role === 'admin' && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-[#D4AF37]/10 text-[#D4AF37]">ADMIN</span>
                    )}
                  </div>
                </div>
              </div>
              {showUserProgress && (
                <>
                  <div className="px-3 py-2.5 bg-gradient-to-br from-[#FFFBF0] to-white border-b border-zinc-100" data-testid="menu-user-progress">
                    <div className="flex items-center gap-2 mb-1.5">
                      <BookOpenCheck className="w-4 h-4 text-[#D4AF37] shrink-0" />
                      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-600">Il tuo percorso</p>
                    </div>
                    {progressLoading ? (
                      <p className="text-xs text-zinc-500 pl-6">Caricamento…</p>
                    ) : (
                      <>
                        {(mainProg?.catalog_total ?? 0) > 0 && (
                          <>
                            <p className="text-[11px] font-medium text-zinc-500 pl-6 uppercase tracking-wide">Formazione</p>
                            <p className="text-sm text-zinc-800 pl-6 leading-snug">
                              <span className="text-zinc-600">Contenuti aperti</span>{' '}
                              <span className="font-semibold tabular-nums text-zinc-900">
                                {mainProg.opened_count ?? 0}/{mainProg.catalog_total}
                              </span>
                              <span className="text-zinc-400 text-xs ml-1">({mainProg.percent_opened ?? 0}%)</span>
                            </p>
                            <p className="text-sm text-zinc-800 pl-6 mt-1 leading-snug">
                              <span className="text-zinc-600">Completati</span>{' '}
                              <span className="font-semibold tabular-nums text-zinc-900">
                                {mainProg.completed_count ?? 0}/{mainProg.catalog_total}
                              </span>
                              <span className="text-zinc-400 text-xs ml-1">({mainProg.percent_completed ?? 0}%)</span>
                            </p>
                          </>
                        )}
                        {(spilleProg?.catalog_total ?? 0) > 0 && (
                          <>
                            <p className={`text-[11px] font-medium text-zinc-500 pl-6 uppercase tracking-wide ${(mainProg?.catalog_total ?? 0) > 0 ? 'mt-3' : ''}`}>Area Spille</p>
                            <p className="text-sm text-zinc-800 pl-6 leading-snug">
                              <span className="text-zinc-600">Contenuti aperti</span>{' '}
                              <span className="font-semibold tabular-nums text-zinc-900">
                                {spilleProg.opened_count ?? 0}/{spilleProg.catalog_total}
                              </span>
                              <span className="text-zinc-400 text-xs ml-1">({spilleProg.percent_opened ?? 0}%)</span>
                            </p>
                            <p className="text-sm text-zinc-800 pl-6 mt-1 leading-snug">
                              <span className="text-zinc-600">Completati</span>{' '}
                              <span className="font-semibold tabular-nums text-zinc-900">
                                {spilleProg.completed_count ?? 0}/{spilleProg.catalog_total}
                              </span>
                              <span className="text-zinc-400 text-xs ml-1">({spilleProg.percent_completed ?? 0}%)</span>
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
              {isAdmin && (
                <>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      navigate('/admin');
                    }}
                    data-testid="admin-menu-item"
                    className="cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowAdminPwdModal(true)} className="cursor-pointer">
                    <Key className="w-4 h-4 mr-2" />
                    Cambia password admin
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem className="text-red-600 focus:text-red-700 cursor-pointer" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {showAdminPwdModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[9999] p-4 pt-16">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto relative mt-8">
            <h2 className="text-xl font-semibold text-zinc-900 mb-4">Cambia password admin</h2>
            <form onSubmit={handleChangeDefaultAdminPassword}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Password attuale</label>
                  <input
                    type="password"
                    value={adminCurrentPassword}
                    onChange={(e) => setAdminCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Nuova password</label>
                  <input
                    type="password"
                    value={adminNewPassword}
                    onChange={(e) => setAdminNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>
                {adminPwdError && <div className="text-red-600 text-sm">{adminPwdError}</div>}
                {adminPwdSuccess && <div className="text-green-600 text-sm">{adminPwdSuccess}</div>}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdminPwdModal(false);
                    setAdminCurrentPassword('');
                    setAdminNewPassword('');
                    setAdminPwdError('');
                    setAdminPwdSuccess('');
                  }}
                  className="flex-1 px-4 py-2 border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8941F] transition-colors"
                >
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {!mediaModalOpen && (
        <NotificationPopup notification={popupNotification} onClose={dismissNotificationPopup} />
      )}
    </header>
  );
};

export default Header;
